import localforage from "localforage";

import type { Decoration } from "./effects";
import type { Rect } from "./source";

/**
 * Konkord: the picture under a decoration, kept on this device, so the
 * decoration can be changed later without the old frame baked in. Keyed
 * by who owns it (user / server id + "avatar" / "icon"); `fileId` is the
 * upload it belongs to: once the avatar changes elsewhere it no longer
 * matches and the current picture is used instead.
 */
export interface KeptOriginal {
  /** the picked file: the cropped still, or the whole animation */
  blob: Blob;
  /** for an animation, the part that was cropped */
  rect?: Rect;
  decoration: Decoration;
  fileId: string;
}

type Pending = Omit<KeptOriginal, "fileId">;

const store = localforage.createInstance({
  name: "konkord",
  storeName: "decorate_originals",
});

/** made by the decoration step, waiting for the save that uploads them */
const pending = new WeakMap<File, Pending>();

export function originalKey(ownerId: string, kind: "avatar" | "icon") {
  return `${ownerId}:${kind}`;
}

/** remember what `made` was made from, until it is uploaded */
export function rememberOriginal(made: File, original: Pending) {
  pending.set(made, original);
}

/** the original of a file made in this session, not uploaded yet */
export function pendingOriginal(made: File) {
  return pending.get(made);
}

/**
 * After the upload: keep the original of `made` as the one under `fileId`
 * (or forget the old one, when the new picture had no decoration step)
 */
export async function keepOriginal(key: string, made: File, fileId: string) {
  const original = pending.get(made);
  if (!original) return store.removeItem(key);
  await store.setItem<KeptOriginal>(key, { ...original, fileId });
}

/** the original kept for the picture uploaded as `fileId`, if any */
export async function loadOriginal(key: string, fileId: string) {
  const kept = await store.getItem<KeptOriginal>(key).catch(() => null);
  return kept?.fileId === fileId ? kept : null;
}

/** file extension for a picture type */
export function extensionOf(type: string | undefined) {
  switch (type) {
    case "image/gif":
      return "gif";
    case "image/jpeg":
      return "jpg";
    case "image/webp":
      return "webp";
    case "image/avif":
      return "avif";
    default:
      return "png";
  }
}

/**
 * Save a file from the server on this device (works in the desktop app
 * too): fetched as a blob, then a download link clicked
 */
export async function downloadFile(
  url: string,
  baseName: string,
  type?: string,
) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${baseName}.${extensionOf(type || blob.type)}`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}
